import { useAuth } from '@clerk/clerk-react';
import { Navigate, Link } from 'react-router-dom';
import { Sparkles, Users, Briefcase, FileText, Link as LinkIcon, Palette } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface FeatureCard {
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly title: string;
  readonly description: string;
}

interface HowItWorksStep {
  readonly number: number;
  readonly title: string;
  readonly description: string;
}

const featureCards: readonly FeatureCard[] = [
  {
    icon: Sparkles,
    title: 'AI-Powered Matching',
    description:
      'Smart scoring across 12 factors plus deep AI analysis with match reasoning and recommendations.'
  },
  {
    icon: Users,
    title: 'Team Skill Analysis',
    description:
      'Visual dashboards showing skill coverage, gaps, and team composition across your entire consulting team.'
  },
  {
    icon: Briefcase,
    title: 'Bulk Sales Matching',
    description:
      'Select profiles and assignments, run matching at scale, and get scored results in seconds.'
  },
  {
    icon: FileText,
    title: 'Assignment Lifecycle',
    description:
      'Track opportunities from discovery to won or lost — with notes, offered prices, and match scores.'
  },
  {
    icon: LinkIcon,
    title: 'Multi-Source Integration',
    description:
      'Connect Verama, Upgraded, Teamtailor, or upload CVs. Eligaro normalizes everything automatically.'
  },
  {
    icon: Palette,
    title: 'Fully Customizable',
    description:
      'Dark mode, color themes, font sizes, compact mode — make Eligaro look and feel the way you want.'
  }
] as const;

const howItWorksSteps: readonly HowItWorksStep[] = [
  {
    number: 1,
    title: 'Connect your sources',
    description:
      'Import assignments from job platforms or upload CVs. Eligaro normalizes and enriches all data automatically.'
  },
  {
    number: 2,
    title: 'AI matches your team',
    description:
      'Smart scoring across skills, experience, industry, and team fit. See exactly why each match works.'
  },
  {
    number: 3,
    title: 'Win more assignments',
    description: 'Generate professional pitches, track applications, and close deals faster.'
  }
] as const;

export function LandingPage() {
  const { isSignedIn, isLoaded } = useAuth();

  if (isLoaded && isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
          <span className="text-lg font-semibold text-primary">Eligaro</span>
          <div className="flex items-center gap-3">
            <Link
              to="/sign-in"
              className="text-sm text-foreground/70 transition-colors hover:text-foreground"
            >
              Sign In
            </Link>
            <Button asChild size="sm">
              <Link to="/sign-up">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="flex flex-col items-center px-4 pt-32 pb-24 text-center sm:px-6 md:pt-40 md:pb-32">
          <h1 className="mx-auto max-w-3xl text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            Find the perfect consultant for every assignment
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Eligaro analyzes skills, experience, and team fit to match your consultants to the right
            assignments — automatically.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg">
              <Link to="/sign-up">Get Started</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#features">Learn More</a>
            </Button>
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-12 text-center text-3xl font-bold">
              Everything you need to match smarter
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((card) => (
                <Card key={card.title} className="p-6">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <card.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold">{card.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {card.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-12 text-center text-3xl font-bold">How it works</h2>
            <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
              {howItWorksSteps.map((step) => (
                <div key={step.number} className="flex flex-col items-center text-center">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
                    {step.number}
                  </div>
                  <h3 className="mb-3 text-lg font-semibold">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-3xl rounded-2xl bg-primary/5 px-8 py-16 text-center">
            <h2 className="mb-4 text-3xl font-bold">Ready to match smarter?</h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Join consulting teams already using Eligaro to win more assignments.
            </p>
            <Button asChild size="lg">
              <Link to="/sign-up">Get Started</Link>
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <p className="text-center text-xs text-muted-foreground">
          &copy; 2026 Eligaro. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
